import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {LanguageService} from 'src/app/shared/components/language/language.service';
import {ILanguage} from 'src/app/shared/components/language/language.model';
import {FormArray, FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {isNil} from 'lodash-es';
import {MultiLangService} from './multi-lang.service';

@Component({
    selector: 'app-multi-lang-input',
    templateUrl: './multi-lang-input.component.html',
    styleUrls: ['./multi-lang-input.component.scss'],
    standalone: false
})
export class MultiLangInputComponent implements OnInit, OnChanges {

  constructor(
    private fb: FormBuilder,
    private languageService: LanguageService,
    private multiLangService: MultiLangService,
  ) { }

  languages: ILanguage[] = [];
  propValuesFormArrayKey: string;

  @Input() formGroup: FormGroup;
  @Input() propName: string;
  @Input() propHolderObject: any;


  ngOnInit(): void {

    this.propValuesFormArrayKey = this.propName + 'formArray';
    this.formGroup.addControl(this.propValuesFormArrayKey, this.fb.array([]));


    this.languageService.getLanguages().subscribe((languages: ILanguage[]) => {
      this.languages = languages;
      if(this.languagePropsFormArray.length === 0) {
        this.addNewLanguageRow();
      }
    });
  }

  ngOnChanges(changes: any) {
    for (let propName in changes) {
      if (propName === 'propHolderObject') {
        if (this.propHolderObject !== undefined) {

          if(this.languagePropsFormArray.controls.length > 0) {
            this.languagePropsFormArray.removeAt(0);
          }

          this.languages.forEach((language: ILanguage) => {
            const propValue = this.multiLangService.getValue(this.propHolderObject, this.propName, language);
            this.addExistingLanguageRow(language, propValue);
          });

          this.formGroup.markAsPristine();
        } else {
          this.addNewLanguageRow();
        }
      }
    }
  }

  private addNewLanguageRow(): void {
    this.languagePropsFormArray?.push(this.newLanguagePropGroup(this.getFirstLanguageNotSet(), this.propName, null));
  }

  private getFirstLanguageNotSet() {
    let lang: ILanguage;
    this.languages.forEach(language => {
      if(isNil(lang) && !this.hasValueInLanguage(language)) {
        lang = language;
      }
    })
    return lang;
  }


  private addExistingLanguageRow(language: ILanguage, langValue: string): void {
    if (langValue && langValue != '') {
      this.languagePropsFormArray.push(this.newLanguagePropGroup(language, this.propName, langValue));
    }
  }

  private newLanguagePropGroup(language: ILanguage, propName: string, propValue: string): FormGroup {
    let group = this.fb.group({
      language: new FormControl(language, []),
    });

    (group as FormGroup).addControl(propName, new FormControl(propValue, []));

    return group;
  }

  private addedLanguages(): string[] {
    return this.languagePropsFormArray.controls.map((control: FormGroup) => { return control.controls.language.value?.code});
  }

  public canAddLanguageProps(): boolean {
    return this.languagePropsFormArray.controls.length < this.languages.length;
  }

  public isLanguageAddedAlready(langCode: string): boolean {
    return this.addedLanguages().includes(langCode);
  }

  public onAddLanguageRow(): void {
    this.addNewLanguageRow();
    this.formGroup.markAsDirty();
  }

  public onRemoveLanguageRow(index: number): void {
    this.languagePropsFormArray.removeAt(index);
    this.formGroup.markAsDirty();
  }

  get languagePropsFormArray(): FormArray {
    return this.formGroup.get(this.propValuesFormArrayKey) as FormArray;
  }

  hasValueInAtLeastOneLanguage(): boolean {
    let hasValue = false;
    this.languagePropsFormArray.controls.forEach((formGroup: any) => {
      const propValue = formGroup.value[this.propName];
      const lang = formGroup.value.language;

      if (this.areValueAndLanguageSet(propValue, lang)) {
        hasValue = true;
      }
    });
    return hasValue;
  }

  hasValueInLanguage(language: ILanguage): boolean {
    let hasValue = false;
    this.languagePropsFormArray.controls.forEach((formGroup: any) => {
      const propValue = formGroup.value[this.propName];
      const lang = formGroup.value.language;

      if (this.areValueAndLanguageSet(propValue, lang)) {
        hasValue = true;
      }
    });
    return hasValue;
  }

  populateObjectValues(object: any) {
    const currentLanguageCodes = new Set<string>();
    
    this.languagePropsFormArray.controls.forEach((formGroup: any) => {
      const propValue = formGroup.value[this.propName];
      const lang = formGroup.value.language;

      if (this.areValueAndLanguageSet(propValue, lang)) {
        this.multiLangService.setValue(object, this.propName, propValue, lang);
        currentLanguageCodes.add(lang.code);
      }
    });

    this.languages.forEach((language: ILanguage) => {
      if (!currentLanguageCodes.has(language.code)) {
        this.multiLangService.setValue(object, this.propName, '', language);
      }
    });
  }

  private areValueAndLanguageSet(value?: string, lang?: ILanguage) {
    return value && value.trim() !== '' && lang?.code
  }
}
